import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  InputNumber,
  Checkbox,
  Dropdown,
  Menu,
  Tag,
  Tooltip,
} from 'antd';
import { EditOutlined, MoreOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { PARTNER_SERVICES } from '../../constants/EntityName';
import callApi from '../../Api/config';

import PartnerPackageSelection from './PartnerPackageSelection';
import ConfirmationModal from '../../components/ConfirmationModal';
import { InvertFillButton } from '../../components/InvertFillButton';
import { toastError, toastSuccess } from '../../service/Toast';

interface PartnerAssignedPackagesProps {
  partnerId: string;
  partnerName: string;
}

const PartnerAssignedPackages: React.FC<PartnerAssignedPackagesProps> = ({
  partnerId,
  partnerName,
}) => {
  const [assignedPackages, setAssignedPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null);
  const [editingPrice, setEditingPrice] = useState<number | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<any>(null);

  const fetchAssignedPackages = async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const res = await callApi(
        `${PARTNER_SERVICES}?partnerId=${partnerId}&page=1&limit=500`,
        'GET'
      );
      if (res?.status) {
        const results =
          res.data?.data?.results ||
          res.data?.results ||
          (Array.isArray(res.data?.data) ? res.data.data : []);
        setAssignedPackages(results);
      }
    } catch (error) {
      console.error('Failed to fetch assigned packages', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (partnerId) {
      fetchAssignedPackages();
    }
  }, [partnerId]);

  // Helper to try parsing JSON strings if necessary
  const parseValue = (val: any) => {
    if (typeof val === 'string') {
      try {
        return JSON.parse(val);
      } catch (e) {
        return val; // return as string if not valid JSON
      }
    }
    return val;
  };

  const getParameterTags = (record: any) => {
    const params: { label: string; values: string[] }[] = [];
    const pkg = record.package || record; // Handle if passing record directly or package object

    // Key fields to check
    const keysToCheck = [
      { key: 'applyingFrom', label: 'Applying From' },
      { key: 'citizenship', label: 'Citizenship' },
      { key: 'destination', label: 'Destination' },
      { key: 'country', label: 'Country' },
      { key: 'visaType', label: 'Visa Type' },
      { key: 'processingTime', label: 'Processing Time' },
    ];

    keysToCheck.forEach(({ key, label }) => {
      let val = pkg[key];
      if (!val && record[key]) val = record[key]; // detailed fallback

      if (val) {
        const parsed = parseValue(val);
        const values = Array.isArray(parsed) ? parsed : [String(parsed)];
        params.push({ label, values });
      }
    });

    return params;
  };

  const handleUpdatePrice = async (id: string, newPrice: number | null) => {
    try {
      const res = await callApi(`${PARTNER_SERVICES}/${id}`, 'PATCH', {
        customPrice: newPrice,
      });
      if (res?.status && (res?.data?.isSuccess || res?.data?.statusCode === 200)) {
        toastSuccess('Custom price updated successfully');
        setEditingPriceId(null);
        setEditingPrice(null);
        fetchAssignedPackages();
      } else {
        toastError('Failed to update custom price');
      }
    } catch (error) {
      toastError('Failed to update custom price');
    }
  };

  // Documitra Fee update logic removed as it is now read-only
  const handleUpdateReadyToShip = async (id: string, isReadyToShip: boolean) => {
    // Optimistic update
    const originalPackages = [...assignedPackages];
    setAssignedPackages(prev =>
      prev.map(pkg =>
        pkg.id === id ? { ...pkg, readyToShip: isReadyToShip, isReadyToShip: isReadyToShip } : pkg
      )
    );

    try {
      const res = await callApi(`${PARTNER_SERVICES}/${id}`, 'PATCH', {
        readyToShip: isReadyToShip,
      });
      if (res?.status && (res?.data?.isSuccess || res?.data?.statusCode === 200)) {
        toastSuccess(`Ready to Ship ${isReadyToShip ? 'enabled' : 'disabled'}`);
        fetchAssignedPackages();
      } else {
        // Revert on failure
        setAssignedPackages(originalPackages);
        toastError('Failed to update Ready to Ship status');
      }
    } catch (error) {
      // Revert on error
      setAssignedPackages(originalPackages);
      toastError('Failed to update Ready to Ship status');
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    // Find the record to check global status
    const record = assignedPackages.find(p => p.id === id);

    // Prevent activation if global package is suspended
    if (
      status === 'Active' &&
      (record?.package?.isActive === false || record?.package?.service?.isActive === false)
    ) {
      toastError('Cannot activate this package because the global service is suspended.');
      return;
    }

    try {
      const res = await callApi(`${PARTNER_SERVICES}/${id}`, 'PATCH', {
        status,
      });
      if (res?.status && (res?.data?.isSuccess || res?.data?.statusCode === 200)) {
        toastSuccess(`Package ${status.toLowerCase()} successfully`);
        fetchAssignedPackages();
      } else {
        toastError('Failed to update status');
      }
    } catch (error) {
      toastError('Failed to update status');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await callApi(`${PARTNER_SERVICES}/${id}`, 'DELETE');
      if (res?.status) {
        toastSuccess('Package deleted successfully');
        fetchAssignedPackages();
      }
    } catch (error) {
      toastError('Failed to delete package');
    }
  };

  const confirmDelete = async () => {
    if (recordToDelete) {
      await handleDelete(recordToDelete.id);
      setDeleteModalVisible(false);
      setRecordToDelete(null);
    }
  };

  const startEditingCustomPrice = (record: any) => {
    setEditingPriceId(`custom-${record.id}`);
    setEditingPrice(record.customPrice || 0);
  };

  const cancelEditingPrice = () => {
    setEditingPriceId(null);
    setEditingPrice(null);
  };

  const getActionMenu = (record: any) => (
    <Menu>
      <Menu.Item
        key="active"
        onClick={() => {
          if (record.package?.isActive === false || record.package?.service?.isActive === false) {
            toastError('Cannot activate this package because the global service is suspended.');
            return;
          }
          handleUpdateStatus(record.id, 'Active');
        }}
        disabled={
          record.status === 'Active' ||
          record.package?.isActive === false ||
          record.package?.service?.isActive === false
        }
        title={
          record.package?.isActive === false || record.package?.service?.isActive === false
            ? 'Global service is suspended'
            : ''
        }
      >
        Active
      </Menu.Item>
      <Menu.Item
        key="suspend"
        onClick={() => handleUpdateStatus(record.id, 'Suspended')}
        disabled={record.status === 'Suspended'}
      >
        Suspend
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        key="delete"
        danger
        onClick={() => {
          setRecordToDelete(record);
          setDeleteModalVisible(true);
        }}
      >
        Delete
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Service Name',
      key: 'serviceName',
      width: 250,
      render: (record: any) => {
        // User wants the name set in partner service
        return <span style={{ fontWeight: 500, color: 'black' }}>{record.name || '--'}</span>;
      },
    },
    {
      title: 'Parameters',
      key: 'parameters',
      width: 150,
      render: (record: any) => {
        const params = getParameterTags(record);

        if (params.length === 0) {
          // Fallback to legacy check if nothing found
          const legacy = record.package?.parameters || [];
          if (legacy.length > 0) return legacy.join(', ');
          return <span style={{ color: '#8c8c8c' }}>--</span>;
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {params.map((param, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip
                  title={
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                        maxWidth: '800px',
                        maxHeight: '500px',
                        overflowY: 'auto',
                        padding: '8px',
                      }}
                    >
                      {param.values.map((v, i) => (
                        <Tag key={i} style={{ margin: 0 }}>
                          {v}
                        </Tag>
                      ))}
                    </div>
                  }
                  color="white"
                  overlayStyle={{ maxWidth: '1000px' }}
                  overlayInnerStyle={{ color: 'black', maxWidth: 'none' }}
                >
                  <span
                    style={{
                      fontSize: '13px',
                      color: '#1890ff',
                      cursor: 'pointer',
                      borderBottom: '1px dashed #1890ff',
                    }}
                  >
                    {param.label} ({param.values.length})
                  </span>
                </Tooltip>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Documitra Fee',
      key: 'documitraFee',
      width: 150,
      render: (record: any) => {
        // Read-only as requested
        const currentFee = record.documitraFee || record.package?.plans?.[0]?.price || 0;
        return <span style={{ fontWeight: 500 }}>${Number(currentFee).toFixed(2)}</span>;
      },
    },
    {
      title: 'Custom Price',
      key: 'customPrice',
      width: 180,
      render: (record: any) => {
        const isEditing = editingPriceId === `custom-${record.id}`;
        const currentPrice =
          record.customPrice !== undefined && record.customPrice !== null ? record.customPrice : 0;

        if (isEditing) {
          return (
            <Space>
              <InputNumber
                value={editingPrice}
                onChange={val => setEditingPrice(val)}
                prefix="$"
                min={0}
                style={{ width: 100 }}
                autoFocus
              />
              <Button
                type="text"
                icon={<CheckOutlined />}
                onClick={() => handleUpdatePrice(record.id, editingPrice)}
                size="small"
                style={{ color: '#52c41a' }}
              />
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={cancelEditingPrice}
                size="small"
                danger
              />
            </Space>
          );
        }

        return (
          <Space>
            <span style={{ fontWeight: 500 }}>
              {record.customPrice !== undefined && record.customPrice !== null
                ? `$${Number(record.customPrice).toFixed(2)}`
                : '--'}
            </span>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => startEditingCustomPrice(record)}
              size="small"
            />
          </Space>
        );
      },
    },
    {
      title: 'Ready to Ship',
      key: 'readyToShip',
      width: 150,
      align: 'center' as const,
      // align: 'center',
      render: (record: any) => (
        <Checkbox
          style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}
          checked={!!(record.readyToShip ?? record.isReadyToShip)}
          onChange={e => handleUpdateReadyToShip(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (record: any) => {
        const status = record.status || 'Active';
        const color =
          status === 'Active' ? '#52c41a' : status === 'Suspended' ? '#faad14' : '#d9d9d9';
        return <span style={{ color, fontWeight: 500 }}>{status}</span>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (record: any) => (
        <Dropdown overlay={getActionMenu(record)} trigger={['click']}>
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div
      style={{
        marginBottom: '20px',
        padding: '20px',
        background: '#fff',
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h3 style={{ margin: 0 }}>Assigned Services</h3>
        <InvertFillButton
          variant="blue"
          size="md"
          fontWeight="semibold"
          onClick={() => {
            console.log('Opening PartnerPackageSelection');
            setModalVisible(true);
          }}
        >
          Assign Services
        </InvertFillButton>
      </div>

      <Table
        dataSource={assignedPackages}
        columns={columns}
        loading={loading}
        pagination={false}
        rowKey="id"
        bordered
        locale={{ emptyText: 'No packages assigned yet.' }}
        scroll={{ x: 1200 }}
      />

      {modalVisible && (
        <PartnerPackageSelection
          partnerId={partnerId}
          onClose={() => setModalVisible(false)}
          onSuccess={() => {
            setModalVisible(false);
            fetchAssignedPackages();
          }}
        />
      )}

      <ConfirmationModal
        open={deleteModalVisible}
        message="Are you sure you want to delete this package?"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModalVisible(false)}
        onClose={() => setDeleteModalVisible(false)}
      />
    </div>
  );
};

export default PartnerAssignedPackages;
